"use client";

/* eslint-disable no-restricted-imports */
import { useRouter as useNextRouter } from "next/navigation";
import { useCallback, useMemo, startTransition } from "react";

import type { TransitionRouter, NavigateOptionsWithTransition } from "./types";
import { useSetFinishViewTransition } from "./view-transitions";

/**
 * Hook that provides a router with view transition support.
 * Compatible with Next.js 16 and React 19.
 */
export function useRouter(): TransitionRouter {
  const router = useNextRouter();
  const finishViewTransition = useSetFinishViewTransition();

  const triggerTransition = useCallback(
    (
      cb: () => void,
      { onTransitionReady }: { onTransitionReady?: () => void } = {}
    ) => {
      if ("startViewTransition" in document) {
        const transition = document.startViewTransition(
          () =>
            new Promise<void>((resolve) => {
              startTransition(() => {
                cb();
                finishViewTransition(() => resolve());
              });
            })
        );

        if (onTransitionReady) {
          transition.ready.then(onTransitionReady);
        }
      } else {
        return cb();
      }
    },
    [finishViewTransition]
  );

  const push = useCallback(
    (href: string, options: NavigateOptionsWithTransition = {}) => {
      const { onTransitionReady, ...routerOptions } = options;
      triggerTransition(() => router.push(href, routerOptions), {
        onTransitionReady,
      });
    },
    [triggerTransition, router]
  );

  const replace = useCallback(
    (href: string, options: NavigateOptionsWithTransition = {}) => {
      const { onTransitionReady, ...routerOptions } = options;
      triggerTransition(() => router.replace(href, routerOptions), {
        onTransitionReady,
      });
    },
    [triggerTransition, router]
  );

  return useMemo(
    () => ({
      ...router,
      push,
      replace,
    }),
    [push, replace, router]
  );
}
